import { reactive, UnwrapRef, ref, Ref, computed, ComputedRef, watch } from '@vue/composition-api';
import i18n, { i18n_t } from '@/locales/i18n';
import userTableHandler from '@/renderer/views/desktop/ManageConsole/UserManagement/innerUser/compositions/listHandler';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import store from '@/renderer/store';
import { checkRoleIsAdmin } from '@/renderer/scripts/utils/UserRole';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

type TeamMemberItem = SMH.TeamMemberItemData & {
  teamRoleDisabled: boolean;
  fileRoleDisabled: boolean;
};
export type TableData = {
  keyword: string;
  list: TeamMemberItem[];
  selectedRowKeys: Array<string>;
  selectedList: TeamMemberItem[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
};

type HandlerParams = {
  rootId?: Ref<number>;
  teamId: Ref<number>;
  teamNoSpace?: Ref<boolean>;
};

type Handler = {
  loading: Ref<boolean>;
  columns: Ref<any[]>;
  tableData: UnwrapRef<TableData>;
  sort: Sort;
  rolesMap: ComputedRef<Map<number, string>>;
  initData: () => void;
  queryMember: (pageSize?: number) => Promise<void>;
  searchMember: () => void;
  refresh: () => void;
  addTeamMember: (userIds: number[], authorizeTo: CoFile.DirAuthorize[]) => Promise<void>;
  removeMember: (userIds: number[], showTips?: boolean) => Promise<void>;
  handleSelectRows: (value: Array<string>, opt: any) => void;
  onPageSizeChange: (pageSize: number) => void;
  onCurrentChange: (current: number) => void;
  sortChange: (newSort: Sort) => void;
  switchSort: (columnName: string) => void;
  editFileRole: (authority: CoFile.DirAuthorize[]) => Promise<boolean>;
  addTeamAdmin: (userIds: string) => Promise<boolean>;
  deleteTeamAdmin: (userIds: string) => Promise<boolean>;
};
export default function teamHandler({ rootId, teamId, teamNoSpace }: HandlerParams): Handler {
  const loading = ref(false);
  const tableData = reactive<TableData>({
    keyword: '',
    selectedRowKeys: [],
    selectedList: [],
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
  });
  const rolesMap = computed(() => {
    const roleList: SMH.Role[] = store.getters.roles;
    const map = new Map<number, string>();
    roleList.forEach(item => {
      map.set(item.id, item.name);
    });
    return map;
  });
  const columns = computed(() => {
    if (!rootId) return [];

    let tableColumns;
    if (teamId.value === rootId.value) {
      tableColumns = [
        {
          colKey: 'row-select',
          type: 'multiple',
          width: 50,
          fixed: 'left',
          checkProps: ({ row }: { row: TeamMemberItem }) => ({
            disabled: row.fileRoleDisabled,
          }),
        },
        {
          colKey: 'nickname',
          title: 'title-nickname',
          titleDesc: i18n_t('用户昵称'),
          width: 80,
          sorter: true,
          fixed: 'left',
        },
        { colKey: 'role', title: 'title-role', titleDesc: i18n_t('身份'), width: 80, sorter: true },
        // { colKey: 'space', title: '个人空间', width: 200 },
        {
          colKey: 'fileRole',
          title: 'fileRoleTitle',
          cell: 'file-role-content',
          width: 140,
        },
        {
          colKey: 'teams',
          title: i18n_t('所属团队'),
          width: 80,
        },
        store.getters.onlyPasswordLogin
          ? {
              colKey: 'accountName',
              title: i18n_t('用户账号'),
              width: 100,
            }
          : {
              colKey: 'phoneNumber',
              title: i18n_t('手机号'),
              width: 100,
            },
      ];
    } else {
      tableColumns = [
        { colKey: 'row-select', type: 'multiple', width: 50, fixed: 'left' },
        {
          colKey: 'nickname',
          title: 'title-nickname',
          titleDesc: i18n_t('用户昵称'),
          width: 80,
          sorter: true,
          fixed: 'left',
        },
        { colKey: 'role', title: 'title-role', titleDesc: i18n_t('身份'), width: 80, sorter: true },
        // { colKey: 'space', title: '个人空间', width: 200 },
        {
          colKey: 'fileRole',
          title: 'fileRoleTitle',
          cell: 'file-role-content',
          width: 140,
        },
        {
          colKey: 'teams',
          title: i18n_t('所属团队'),
          width: 80,
        },
        store.getters.onlyPasswordLogin
          ? {
              colKey: 'accountName',
              title: i18n_t('用户账号'),
              width: 100,
            }
          : {
              colKey: 'phoneNumber',
              title: i18n_t('手机号'),
              width: 100,
            },
        {
          colKey: 'op',
          title: i18n_t('操作'),
          width: 80,
        },
      ];
    }
    // 无空间团队需隐藏文件权限列
    if (teamNoSpace && teamNoSpace.value) {
      const fileRoleIndex = tableColumns.findIndex(item => item.colKey === 'fileRole');
      tableColumns.splice(fileRoleIndex, 1);
    }
    // 不允许手机号登录 & 非账密登录需移除手机号列
    if (!store.getters.allowPhoneLogin && !store.getters.onlyPasswordLogin) {
      tableColumns.splice(5, 1);
    }
    return tableColumns;
  });

  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('teamManager', {
    sortBy: 'role',
    descending: true,
  });

  const { methods: listMethods } = userTableHandler();
  const methods = {
    // 初始化数据
    initData() {
      Object.assign(tableData, {
        keyword: '',
        selectedRowKeys: [],
        selectedList: [],
        list: [],
        pagination: {
          current: 1,
          pageSize: tableData.pagination.pageSize,
          total: 0,
        },
      });
    },
    // 查询团队成员
    async queryMember(pageSize?: number) {
      try {
        loading.value = true;
        const res = await smh.userAPI.queryTeamMember({
          teamId: teamId.value,
          keyword: tableData.keyword,
          page: tableData.pagination.current,
          pageSize: pageSize || tableData.pagination.pageSize,
          orderBy: innerSort.value.orderBy,
          orderByType: innerSort.value.orderByType,
          withBelongingTeams: true,
          withSpaceUsage: true,
          showPhoneNumber: true,
        });
        loading.value = false;
        tableData.pagination.total = res.data.totalNum;
        tableData.list = listMethods.formatList(res.data.contents);
        // 团队下的成员列表重新整理
        // 系统管理员、超管不支持修改身份
        // 系统管理员、超管、团队管理员不支持修改文件权限
        tableData.list = tableData.list.map(item => {
          const roleStr = item.teamRole === 'teamAdmin' ? 'teamAdmin' : item.role;
          return {
            ...item,
            role: roleStr,
            teamRoleDisabled: checkRoleIsAdmin(roleStr, true),
            fileRoleDisabled: checkRoleIsAdmin(roleStr),
          };
        });
        tableData.selectedRowKeys = [];
        tableData.selectedList = [];
      } catch (e) {
        loading.value = false;
        tips.error(e as MsgError);
      }
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      methods.queryMember();
    },
    refresh() {
      tableData.selectedRowKeys = [];
      tableData.selectedList = [];
      methods.queryMember();
    },
    searchMember() {
      tableData.pagination.current = 1;
      methods.refresh();
    },
    // 添加团队成员
    async addTeamMember(userIds: number[], authorizeTo: CoFile.DirAuthorize[]) {
      // eslint-disable-next-line no-useless-catch
      try {
        await smh.userAPI.addTeamMember({
          teamId: teamId.value,
          userIds,
          authorizeTo,
        });
      } catch (e) {
        throw e;
      }
    },
    /**
     * 删除团队成员,默认显示tips
     * @param userIds [xx,xx].join(',')
     * @param showTips 默认为true显示 若false不显示
     */
    async removeMember(userIds: number[], showTips = true) {
      try {
        showTips && tips.loading(i18n_t('正在移除...'));
        await smh.userAPI.deleteTeamMember({
          teamId: teamId.value,
          userIds,
        });
        showTips && tips.success(i18n_t('移除成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 修改成员文件权限
    async editFileRole(authorizeTo: CoFile.DirAuthorize[]) {
      let editSuccessful = false;
      try {
        tips.loading(i18n_t('正在修改...'));
        await smh.userAPI.editTeamMemberFileRole({
          teamId: teamId.value,
          authorizeTo,
        });
        editSuccessful = true;
        tips.success(i18n_t('修改成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
      return editSuccessful;
    },
    // 添加团队管理员
    async addTeamAdmin(userIds: string) {
      let editSuccessful = false;
      try {
        tips.loading(i18n_t('正在修改...'));
        await smh.userAPI.addTeamAdmin({
          teamId: teamId.value,
          userIds,
        });
        editSuccessful = true;
        tips.success(i18n_t('修改成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
      return editSuccessful;
    },
    // 删除团队管理员
    async deleteTeamAdmin(userIds: string) {
      let editSuccessful = false;
      try {
        tips.loading(i18n_t('正在修改...'));
        await smh.userAPI.deleteTeamAdmin({
          teamId: teamId.value,
          userIds,
        });
        editSuccessful = true;
        tips.success(i18n_t('修改成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
      return editSuccessful;
    },
    handleSelectRows(value: Array<string>, { selectedRowData }: any) {
      tableData.selectedRowKeys = value;
      tableData.selectedList = selectedRowData;
    },
    onPageSizeChange(pageSize: number) {
      tableData.pagination.pageSize = pageSize;
      methods.queryMember();
    },
    onCurrentChange(current: number) {
      tableData.pagination.current = current;
      methods.queryMember();
    },
  };
  watch(
    () => tableData.pagination.total,
    val => {
      if (!val) return;
      const newPageNum = Math.ceil(val / tableData.pagination.pageSize);
      if (tableData.pagination.current > newPageNum) {
        tableData.pagination.current = newPageNum;
        methods.refresh();
      }
    },
  );
  return {
    loading,
    columns,
    sort: tableSort,
    tableData,
    rolesMap,
    ...methods,
  };
}
